import { INTENSITY, PRIVACY_FILLED_INDICES } from "./intensity";
import { cn } from "@/lib/utils";

// Cell centers for a 52-column grid: (idx + 0.5) / 52 × 100%. Aligned with
// engineering L4 anchors (cols 7, 20, 32, 46) — privacy reviews live on a
// different cadence and are NOT aligned with these. colIndex drives the
// scroll-fill timing so each annotation lands as its peak fills in.
const ANNOTATIONS = [
  { label: "EU Expansion",             period: "Q1 · Feb", percent: "14.42%", category: "region",  colIndex: 7  },
  { label: "Third-Party Integration",  period: "Q2 · May", percent: "39.42%", category: "vendor",  colIndex: 20 },
  { label: "AI Agents Launched",       period: "Q3 · Aug", percent: "62.5%",  category: "ai",      colIndex: 32 },
  { label: "Retention Period Changed", period: "Q4 · Nov", percent: "89.42%", category: "privacy", colIndex: 46 },
] as const;

const CATEGORY_BORDER: Record<(typeof ANNOTATIONS)[number]["category"], string> = {
  region: "border-annot-region",
  vendor: "border-annot-vendor",
  ai: "border-annot-ai",
  privacy: "border-annot-privacy",
};

const MONTHS = [
  "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
  "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
] as const;

// Scroll-fill range. The outer pin is 180vh tall in a 100vh viewport, so view-
// cover progresses 0→100% over 280vh of scroll. Sticky pin engages around
// cover 36% (outer's top hits viewport top) and disengages around cover 64%
// (bottom hits viewport top). Cells must fill INSIDE that ~28% window for the
// animation to play while the section is visually pinned. Range: cover 38-62%
// (24% span) with a small buffer at each edge.
function cellRange(colIndex: number): { start: string; end: string } {
  const start = 38 + (colIndex / 51) * 18;
  return { start: `${start.toFixed(2)}%`, end: `${(start + 6).toFixed(2)}%` };
}

// Empty cell base styling — always visible, forms the static grid skeleton.
const EMPTY_CELL_CLASS = "relative aspect-square rounded-[2px] bg-sage-empty border border-sage-empty-border";
const HEATMAP_SCROLL_CLASS = "-mx-1 overflow-x-auto px-1 pb-2 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0";
const HEATMAP_CONTENT_CLASS = "min-w-[760px] sm:min-w-0";
const VELOCITY_LABEL_CLASS = "sticky left-0 z-10 mb-3 w-fit bg-background pr-3 text-sm font-semibold text-foreground sm:static sm:bg-transparent sm:pr-0";

function MonthAxis({ className }: { className?: string }) {
  return (
    <>
      <div
        aria-hidden="true"
        className={cn(
          "mt-2 grid grid-cols-4 text-[12px] font-mono uppercase tracking-[0.06em] text-foreground-muted sm:hidden",
          className,
        )}
      >
        {["Q1", "Q2", "Q3", "Q4"].map((q) => (
          <span key={q} className="text-center">{q}</span>
        ))}
      </div>
      <div
        aria-hidden="true"
        className={cn(
          "mt-2 hidden grid-cols-12 text-[12px] font-mono uppercase tracking-[0.06em] text-foreground-muted sm:grid",
          className,
        )}
      >
        {MONTHS.map((m) => (
          <span key={m} className="text-center">{m}</span>
        ))}
      </div>
    </>
  );
}

export function ReframeGrid({ className }: { className?: string }) {
  return (
    <div className={cn("max-w-[1120px] mx-auto", className)}>
      <h2
        id="reframe-heading"
        className="font-heading text-[2.25rem] font-semibold tracking-[-0.025em] leading-[1.08] text-foreground text-center"
      >
        Daily deployments. Intermittent reviews.
      </h2>
      <p className="mx-auto mt-4 max-w-2xl text-center text-base leading-relaxed text-foreground-muted">
        Systems change every day. Privacy reviews still happen in snapshots.
        Risk builds in the gaps.
      </p>

      <div className={cn("mt-8 sm:mt-12", HEATMAP_SCROLL_CLASS)}>
        <div className={HEATMAP_CONTENT_CLASS}>
          <div>
            <p className={VELOCITY_LABEL_CLASS}>Engineering Velocity</p>

            <div className="relative pt-12">
              <div
                role="img"
                aria-label="Engineering velocity: 52 weeks of frequent deployments."
                className="grid gap-[2px]"
                style={{ gridTemplateColumns: "repeat(52, minmax(0, 1fr))" }}
              >
                {INTENSITY.map((level, i) => {
                  if (level === 0) {
                    return (
                      <div key={i} data-cell aria-hidden="true" className={EMPTY_CELL_CLASS} />
                    );
                  }
                  const { start, end } = cellRange(i);
                  return (
                    <div key={i} data-cell aria-hidden="true" className={EMPTY_CELL_CLASS}>
                      <div
                        data-cell-fill
                        aria-hidden="true"
                        className={cn(
                          "absolute -inset-px rounded-[2px]",
                          `bg-sage-l${level} border border-sage-l${level}-border`,
                        )}
                        style={{
                          ["--cell-start" as string]: start,
                          ["--cell-end" as string]: end,
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              {ANNOTATIONS.map((a) => {
                const { start, end } = cellRange(a.colIndex);
                return (
                  <div
                    key={a.label}
                    data-annotation
                    aria-hidden="true"
                    style={{
                      left: a.percent,
                      ["--cell-start" as string]: start,
                      ["--cell-end" as string]: end,
                    }}
                    className={cn(
                      "absolute top-0 -translate-x-1/2",
                      "bg-surface px-2 py-1 rounded-[2px] border",
                      CATEGORY_BORDER[a.category],
                      "text-[12px] font-medium text-foreground whitespace-nowrap",
                      "before:content-[''] before:absolute before:left-1/2 before:-translate-x-1/2",
                      "before:top-full before:w-px before:h-5 before:bg-border",
                    )}
                  >
                    {a.label}
                  </div>
                );
              })}
            </div>

            <MonthAxis />
          </div>

          <div className="mt-10">
            <p className={VELOCITY_LABEL_CLASS}>Privacy Reviews</p>
            <div
              role="img"
              aria-label="Privacy reviews: 4 reviews per year."
              className="grid gap-[2px]"
              style={{ gridTemplateColumns: "repeat(52, minmax(0, 1fr))" }}
            >
              {Array.from({ length: 52 }, (_, i) => {
                const filled = PRIVACY_FILLED_INDICES.includes(i);
                if (!filled) {
                  return (
                    <div key={i} data-cell aria-hidden="true" className={EMPTY_CELL_CLASS} />
                  );
                }
                const { start, end } = cellRange(i);
                return (
                  <div key={i} data-cell aria-hidden="true" className={EMPTY_CELL_CLASS}>
                    <div
                      data-cell-fill
                      aria-hidden="true"
                      className="absolute -inset-px rounded-[2px] bg-sage-l3 border border-sage-l3-border"
                      style={{
                        ["--cell-start" as string]: start,
                        ["--cell-end" as string]: end,
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <MonthAxis />
          </div>
        </div>
      </div>

      <ul aria-label="Annotations" className="sr-only">
        {ANNOTATIONS.map((a) => (
          <li key={a.label}>
            {a.label}, {a.period}
          </li>
        ))}
      </ul>

      {/* Second h2 in this section — labels the bottom reframe subsection.
          Intentionally no id; the section's aria-labelledby resolves to the
          top h2#reframe-heading at line 76. */}
      <h2 className="mt-16 mb-4 text-center font-heading text-[2.5rem] sm:text-[2.75rem] font-semibold tracking-[-0.03em] leading-[1.05] text-foreground">
        Good intentions don&apos;t scale.
      </h2>
      <p className="mx-auto max-w-2xl text-center text-base leading-relaxed text-foreground-muted">
        Privacy teams need to know what workloads exist and when they change.
      </p>
    </div>
  );
}
